/**
 * useMarketTypes Hook
 *
 * Fetches market types from the database for use in dropdowns and filters.
 * Replaces the hardcoded MARKET_TYPES constant.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface MarketType {
  id: string;
  name: string;
  slug: string;
}

interface MarketTypesResponse {
  success: boolean;
  data: MarketType[];
}

interface MarketTypeNamesResponse {
  success: boolean;
  data: string[];
}

/**
 * Hook to fetch all market types with full details
 *
 * @returns Market types array with id, name, and slug
 */
export function useMarketTypes() {
  return useQuery({
    queryKey: ['marketTypes'],
    queryFn: async (): Promise<MarketType[]> => {
      const response = await api.get<MarketTypesResponse>('/api/lookup/market-types');
      return response.data;
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - market types rarely change, refetchQueries() forces updates
    gcTime: 24 * 60 * 60 * 1000, // 24 hours cache
  });
}

/**
 * Hook to fetch just market type names as a string array
 * Useful for simple select/dropdown components
 *
 * @returns Array of market type names
 */
export function useMarketTypeNames() {
  return useQuery({
    queryKey: ['marketTypes', 'names'],
    queryFn: async (): Promise<string[]> => {
      const response = await api.get<MarketTypeNamesResponse>('/api/lookup/market-types/names');
      return response.data;
    },
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - market types rarely change, refetchQueries() forces updates
    gcTime: 24 * 60 * 60 * 1000, // 24 hours cache
  });
}

export default useMarketTypes;
